import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { adminApi, formatApiError } from "../auth";
import { Trash2, Plus, Save, ArrowLeft } from "lucide-react";

const EMPTY = {
  name: "",
  category: "engine",
  part_number: "",
  brand: "",
  condition: "Used",
  price: 0,
  stock: 1,
  fitment: [],
  description: "",
  image: "",
  gallery: [],
  warranty: "",
  is_visible: true,
};

const AdminPartForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [taxonomy, setTaxonomy] = useState({ categories: [], conditions: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    adminApi.get("/parts/taxonomy").then((r) => setTaxonomy(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    adminApi.get(`/parts/${id}`).then((r) => setForm({ ...EMPTY, ...r.data }))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateFitment = (i, k, v) => setForm((f) => ({
    ...f,
    fitment: f.fitment.map((row, idx) => idx === i ? { ...row, [k]: v === "" ? null : (["year_from", "year_to"].includes(k) ? Number(v) : v) } : row),
  }));
  const addFitment = () => setForm((f) => ({ ...f, fitment: [...f.fitment, { make: "", model: "", year_from: null, year_to: null }] }));
  const removeFitment = (i) => setForm((f) => ({ ...f, fitment: f.fitment.filter((_, idx) => idx !== i) }));

  const onSave = async () => {
    setErr("");
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        stock: Number(form.stock),
        // Filter out empty fitment rows
        fitment: form.fitment.filter((r) => r.make && r.model),
      };
      if (isEdit) {
        await adminApi.put(`/parts/${id}`, payload);
      } else {
        const r = await adminApi.post("/parts", payload);
        navigate(`/admin/parts/${r.data.id}`);
        return;
      }
      alert("Saved.");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div><p className="vd-muted">Loading…</p></div>;

  return (
    <div data-testid="admin-part-form">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin/parts" className="inline-flex items-center gap-1.5 text-sm vd-muted hover:text-[var(--primary-red)]" data-testid="part-form-back"><ArrowLeft size={14} /> Back</Link>
        <h1 style={{ fontSize: 26 }}>{isEdit ? "Edit part" : "Add new part"}</h1>
      </div>

      {err && <p className="text-sm mb-3" style={{ color: "var(--primary-red)" }} data-testid="part-form-error">{err}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="vd-card p-6 space-y-4">
          <Field label="Name *"><input className="vd-input" value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="part-input-name" /></Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Category *">
              <select className="vd-select" value={form.category} onChange={(e) => update("category", e.target.value)} data-testid="part-input-category">
                {taxonomy.categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Condition *">
              <select className="vd-select" value={form.condition} onChange={(e) => update("condition", e.target.value)} data-testid="part-input-condition">
                {(taxonomy.conditions.length ? taxonomy.conditions : ["New", "Used"]).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Brand"><input className="vd-input" value={form.brand} onChange={(e) => update("brand", e.target.value)} data-testid="part-input-brand" /></Field>
            <Field label="Part Number"><input className="vd-input" value={form.part_number} onChange={(e) => update("part_number", e.target.value)} data-testid="part-input-partnumber" /></Field>
            <Field label="Price (£) *"><input type="number" min="0" step="0.01" className="vd-input" value={form.price} onChange={(e) => update("price", e.target.value)} data-testid="part-input-price" /></Field>
            <Field label="Stock *"><input type="number" min="0" className="vd-input" value={form.stock} onChange={(e) => update("stock", e.target.value)} data-testid="part-input-stock" /></Field>
            <Field label="Warranty"><input className="vd-input" value={form.warranty} onChange={(e) => update("warranty", e.target.value)} data-testid="part-input-warranty" /></Field>
            <Field label="Image URL"><input className="vd-input" value={form.image} onChange={(e) => update("image", e.target.value)} data-testid="part-input-image" placeholder="/api/static/… or https://…" /></Field>
          </div>

          <Field label="Description">
            <textarea rows={4} className="vd-input" value={form.description} onChange={(e) => update("description", e.target.value)} data-testid="part-input-description" />
          </Field>

          {/* Fitment editor */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="vd-heading font-semibold text-sm">Fitment (leave empty for universal)</h3>
              <button type="button" onClick={addFitment} className="vd-btn-secondary text-sm" data-testid="part-add-fitment">
                <Plus size={14} /> Add fitment
              </button>
            </div>
            {form.fitment.length === 0 && <p className="vd-muted text-sm">No specific fitment — part will be shown as universal.</p>}
            <div className="space-y-2">
              {form.fitment.map((row, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_100px_100px_40px] gap-2 items-center" data-testid={`part-fitment-row-${i}`}>
                  <input className="vd-input" placeholder="Make" value={row.make || ""} onChange={(e) => updateFitment(i, "make", e.target.value)} />
                  <input className="vd-input" placeholder="Model" value={row.model || ""} onChange={(e) => updateFitment(i, "model", e.target.value)} />
                  <input type="number" className="vd-input" placeholder="From" value={row.year_from ?? ""} onChange={(e) => updateFitment(i, "year_from", e.target.value)} />
                  <input type="number" className="vd-input" placeholder="To" value={row.year_to ?? ""} onChange={(e) => updateFitment(i, "year_to", e.target.value)} />
                  <button type="button" onClick={() => removeFitment(i)} className="p-2 rounded hover:bg-[var(--bg-secondary)]" title="Remove"><Trash2 size={14} color="var(--primary-red)" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="vd-card p-5 h-fit space-y-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="vd-checkbox" checked={form.is_visible} onChange={(e) => update("is_visible", e.target.checked)} data-testid="part-input-visible" />
            <span className="vd-body text-sm">Visible on storefront</span>
          </label>

          <button onClick={onSave} disabled={saving} className="vd-btn-primary w-full" data-testid="part-form-save">
            <Save size={16} /> {saving ? "Saving…" : (isEdit ? "Save changes" : "Create part")}
          </button>
          {isEdit && <Link to="/admin/parts" className="vd-btn-secondary w-full">Back to list</Link>}
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="vd-muted text-xs uppercase tracking-wider mb-1 block">{label}</span>
    {children}
  </label>
);

export default AdminPartForm;
